import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wind, Palette, Flower, PencilLine, ArrowRight, Flask,
} from "@phosphor-icons/react";
import FrequencyPlayer from "../components/FrequencyPlayer";
import WhatToWear from "../components/WhatToWear";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FROM_COLORS = {
  Stressed: "#E76F51",
  "Burned Out": "#F4A261",
  Anxious: "#CDB4DB",
  "Low Confidence": "#457B9D",
};

export default function MoodFormulasPage() {
  const [formulas, setFormulas] = useState([]);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/formulas`, { withCredentials: true })
      .then((res) => setFormulas(res.data))
      .catch(console.error);
  }, []);

  return (
    <div data-testid="mood-formulas-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#2D6A4F] mb-2">Mood Formulas</p>
        <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-[#1A2421] tracking-tight">
          Emotional Transitions
        </h1>
        <p className="text-sm text-[#78857F] mt-2 max-w-lg">
          Pre-built multi-sensory formulas to help you shift from one emotional state to another.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {formulas.map((f, i) => {
          const isExpanded = expanded === f.id;
          const fromColor = FROM_COLORS[f.from_state] || "#78857F";

          return (
            <motion.div
              key={f.id}
              data-testid={`formula-card-${f.id}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              layout
              className="card-neuro p-6 cursor-pointer relative overflow-hidden"
              onClick={() => setExpanded(isExpanded ? null : f.id)}
            >
              {/* Background accent */}
              <div
                className="absolute top-0 right-0 w-24 h-24 rounded-full blur-[50px] opacity-15"
                style={{ backgroundColor: f.color?.hex || "#84A98C" }}
              />

              <div className="relative z-10">
                {/* Header */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-[#2D6A4F]/10 flex items-center justify-center">
                    <Flask size={22} weight="duotone" className="text-[#2D6A4F]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-['Outfit'] text-lg font-medium text-[#1A2421]">{f.name}</h3>
                  </div>
                </div>

                {/* Transition Badge */}
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="px-2.5 py-1 rounded-full text-xs font-medium text-white"
                    style={{ backgroundColor: fromColor }}
                  >
                    {f.from_state}
                  </span>
                  <ArrowRight size={14} className="text-[#78857F]" />
                  <span
                    className="px-2.5 py-1 rounded-full text-xs font-medium text-white"
                    style={{ backgroundColor: f.color?.hex || "#84A98C" }}
                  >
                    {f.to_state}
                  </span>
                </div>

                <p className="text-sm text-[#78857F] mb-4">{f.description}</p>

                {/* Expanded details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-4 overflow-hidden"
                    >
                      <div className="pt-4 border-t border-[#E1D9CD]">
                        <div className="flex items-center gap-2 mb-2">
                          <Wind size={18} weight="duotone" className="text-[#2D6A4F]" />
                          <span className="text-xs font-semibold text-[#1A2421] uppercase tracking-wider">Breathwork</span>
                        </div>
                        <ol className="space-y-1 pl-6">
                          {f.breathwork?.map((s, j) => (
                            <li key={j} className="text-sm text-[#78857F] list-decimal">{s}</li>
                          ))}
                        </ol>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-semibold text-[#1A2421] uppercase tracking-wider">Sound Frequency</span>
                        </div>
                        <div className="pl-6">
                          <FrequencyPlayer
                            frequencyText={f.sound_frequency}
                            accentColor={f.color?.hex || "#84A98C"}
                          />
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Palette size={18} weight="duotone" className="text-[#CDB4DB]" />
                          <span className="text-xs font-semibold text-[#1A2421] uppercase tracking-wider">Color</span>
                        </div>
                        <div className="flex items-center gap-3 pl-6">
                          <div
                            className="w-8 h-8 rounded-lg border border-black/5"
                            style={{ backgroundColor: f.color?.hex }}
                          />
                          <span className="text-sm text-[#78857F]">{f.color?.name}</span>
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Flower size={18} weight="duotone" className="text-[#F4A261]" />
                          <span className="text-xs font-semibold text-[#1A2421] uppercase tracking-wider">Scent</span>
                        </div>
                        <p className="text-sm text-[#78857F] pl-6">{f.scent}</p>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <PencilLine size={18} weight="duotone" className="text-[#457B9D]" />
                          <span className="text-xs font-semibold text-[#1A2421] uppercase tracking-wider">Journal Prompt</span>
                        </div>
                        <p className="text-sm text-[#78857F] pl-6 italic">"{f.journaling_prompt}"</p>
                      </div>

                      {/* What to Wear */}
                      <div className="pl-6">
                        <WhatToWear mood={f.from_state} />
                      </div>

                      {f.affirmation && (
                        <div className="p-3 rounded-xl bg-[#2D6A4F]/5 border border-[#2D6A4F]/10">
                          <p className="text-center text-sm font-medium text-[#2D6A4F] italic">
                            "{f.affirmation}"
                          </p>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                <p className="text-xs text-[#84A98C] font-medium mt-3">
                  {isExpanded ? "Tap to collapse" : "Tap to explore formula"}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
